/**
 * The 119 endpoint keys that are identical in apps/web and apps/mobile — same URL AND same
 * function source. Derived and verified by `node scripts/endpoints-diff.mjs`.
 *
 * NO IMPORTS, by construction: the only two constants either app's endpoints file needs
 * (`BRANCH_OPTIONS`, `ORDER_SOURCE`) are read exclusively by divergent keys, which live in
 * the platform files.
 *
 * ⚠️ Platform files must compose this PER SECTION, never with a shallow spread:
 *
 *     { ...sharedEndpoint, POS: { ...sharedEndpoint.POS, webOnlyKey } }   ✅
 *     { ...sharedEndpoint, POS: { webOnlyKey } }                          ❌ drops 13 keys
 *
 * The wrong form typechecks, builds green, and 404s on a live terminal. `endpoints-diff.mjs`
 * comparing old-vs-new key-for-key is what catches it — run it, do not eyeball this.
 *
 * Excluded from here because they differ per platform (see each platform file):
 *   ITEMSTATUS.getAllInStockItems · OUTLETSTATUS.toggleBranch
 *   NOTIFICATIONS.testNotification
 *
 * (`POS.getDetailedCustomerInfo` was on that list until 2026-09-05 — see its note below.)
 */
export const sharedEndpoint = {
  POS: {
    getPOSSettings: () => `entity/possettings`,
    getCatalog: () => `catalog/reference/sale`,
    getItems: () => "item/branch/fetchAll",
    getOptionSets: () => "optionset/branch/fetch",
    getDiscounts: () => "discount/branch/fetchAll",
    getBizBrands: () => "business/brand/fetchAll",
    getCustomerInfo: () => "customer/filter",
    getCustomerSummary: (customerRefId: string) => `customer/${customerRefId}/sale/summary`,
    /**
     * The customer's redeemed partner coupons, one page at a time.
     *
     * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, when `packages/ui`'s CustomerSummary
     * took the Redeemed Coupons tab to both platforms. The endpoint was never web-specific —
     * it was only ever reached from a web-only surface. `useRedeemedCoupons` is the one caller.
     */
    getXenoCouponRedeemedData: (phoneNumber: string, pageNumber: number) =>
      `customer/couponRedeemHistory/${phoneNumber}?pageNo=${pageNumber}`,
    /**
     * ⚠️ Was DIVERGENT until 2026-09-05: web took an options object, mobile took a bare id.
     * They build the SAME url for a one-argument call (`customer/<ref>/fetch`), so the web
     * form subsumes mobile's and both apps now share this one. Mobile needs the `external`
     * param for the loyalty consent flow's customer re-read.
     */
    getDetailedCustomerInfo: (
      ref: string,
      { external = false, channelDefId }: { external?: boolean; channelDefId?: string } = {},
    ) => {
      // 🔴 Built by hand, NOT with `URLSearchParams` as the web-only version was. React
      // Native's polyfill implements only `append` and `toString` — `set` THROWS
      // ("URLSearchParams.set is not implemented"), so the web body would have crashed every
      // customer fetch on mobile while typechecking and building green.
      const params: string[] = [];
      if (external) params.push('external=true');
      if (channelDefId) params.push(`channelDefId=${encodeURIComponent(channelDefId)}`);
      const qs = params.join('&');
      return `customer/${ref}/fetch${qs ? `?${qs}` : ''}`;
    },
    authenticateCustomer: () => 'customer/authenticate',
    saveSaleData: () => 'sale/save',
    getItemTag: () => 'entity/itemtag/fetchAll',
    saveCustomer: () => 'customer/save',
    // `includeLicences` adds each branch's `availableLicences` (read by `commonComponents/LeftMenu/branchLicences`)
    getAllBranches: ({ includeLicences = false }: { includeLicences?: boolean } = {}) =>
      includeLicences ? `branch/filter?includeLicences=true` : `branch/filter`,
    getMasterDataPicklist: (listType: string) => `masterdata/picklist/fetch?listType=${listType}`,
  },
  SALE: {
    saveSale: () => `sale/save`,
    // web's key, shared since 2026-09-29: My Orders v2 re-opens a closed sale on both platforms
    saleReopen: (saleId: string) => `sale/${saleId}/reopen`,
  },
  // web's section, shared since 2026-09-29: My Orders v2's back-order sheet reads an item's stock on both platforms
  INVENTORY: {
    getInventory: (itemId: string, branchId: string) => `/item/inventory/fetch?itemId=${itemId}&branchId=${branchId}`,
  },
  /**
   * The kiosk's two merchant-setup reads (rista-be `sales-services`, both under the `Kiosk`
   * licence module). Called from `redux-store/saga/kioskConfig`.
   *
   * ⚠️ Its own section rather than a key under `POS`: the device record is answered per DEVICE,
   * not per branch or per operator, and grouping it with the branch-wide POS calls invites the
   * next reader to fetch it once for the account.
   */
  KIOSK: {
    /**
     * THIS panel's pairing — which card terminal is attached to this box, which UPI provider its
     * QR comes from. GET, no query: the backend looks the pairing up by the `deviceIdentifier`
     * header `apiService` already sends. Answers `null` for a panel nobody has paired.
     */
    getKioskDevice: () => `kioskdevice/fetch`,
    /**
     * The merchant's kiosk configuration, by the id `getBranchData` returns as
     * `kioskConfigurationId`. GET; note the `/fetch` SUFFIX — `kioskconfiguration/<id>` alone
     * is the DELETE route. `REST_INVALID_KIOSK_CONFIGURATION` for an id this business does not
     * own, `REST_ERR_INVALID_INPUT` for one that is not an ObjectId.
     */
    getKioskConfiguration: (kioskConfigurationId: string) =>
      `kioskconfiguration/${kioskConfigurationId}/fetch`,
    /** Merchant-side kiosk pairing endpoints, shared by the web and native setup UI. */
    getPairedKiosks: () => 'kioskdevice/filter',
    saveKioskPairing: () => 'kioskdevice/save',
    getPaymentProviders: () => 'payment/providers/filter',
    getProviderConfiguration: (pid: string) => `payment/provider/${pid}/configuration`,
  },
  COMMON: {
    getBusinessData: () => `preferences/business/detail`,
    getUserData: () => "preferences/user",
    licenceUpdateApiUrl: "/auth/licence",
    getAllPermittedLicenceApiUrl: "preferences/licence",
    updateBranch: "device/branch/update",
    licenceExpiryCheck: () => 'rista/user/expiryreminder',
    getReleaseNotes: () => "releasenotes",
  },
  DISCOUNT: {
    appplyDiscount: () => `coupon/verify`,
    updateAfterClose: (id: string, version: string) => `sale/${id}/discount/update?&version=${version}`,
    /**
     * The partner offers available to this customer against this sale.
     *
     * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, when `packages/core/discounts` took
     * the coupon/deals flow to both platforms. The endpoint was never web-specific — it was
     * only ever reached from a web-only surface, which is hazard 32's distinction. Web's map
     * spreads this section, so its importers are unchanged.
     */
    getExternalDiscounts: () => 'sale/customer/offers',
  },
  REGISTER: {
    getDeviceRegister: () => `device/register`,
    requestRegistrationKey: () => "device/key/request",
    getChangePin: () => "user/cred/reset",
    registerFCM: (fcmToken: string) => `device/android/notifications/${fcmToken}/register`,
  },
  LOGIN: {
    getLogin: () => "auth/login",
    getActiveUsers: () => "session/current/SEP",
    logoutUser: () => `auth/logout`,
    forgotPin: () => "forgotpin",
    fetchOfflineUserList: () => `user/offline/fetchAll`,
  },
  PAYMENT: {
    updatePayment: () => `sale/close`,
    prepaidPaymentApi: () => `customer/prepaid/charge/prepare`,
    saveSaleApi: () => `sale/save`,
    paymentClose: (id: string) => `sale/${id}/payment/close`,
    updateClosedSalePayment: (id: string) => `sale/${id}/payment/save`,
    salePaymentSave: () => `sale/payment/save`,
    extPaymentStatus: () => `payment/ext/status`,
    extPaymentCancel: () => `payment/ext/cancel`,
  },
  LOYALTY: {
    apiLoyaltyProvider: () => `marketing/providers`,
    apiLoyaltyDetails: () => `sale/customer/loyalty`,
    apiLoyaltyRedeem: () => `sale/customer/loyalty/redeem`,
    apiVerifyOtp: () => `sale/customer/loyalty/verify`,
    loyaltyRefund: () => `sale/customer/loyalty/refund`,
    // Enrolment consent + the per-channel programme. All four MOVED from index.desktop.ts, by
    // two independent cross-platform callers a day apart: the kiosk's contact flow took the two
    // consent-OTP keys on 2026-09-04, and `packages/core/loyalty` took the other two on
    // 2026-09-05 when the mobile app adopted that flow. One flow, one endpoint table.
    //
    // Identical strings, so the web map is unchanged — it spreads this section before its own
    // additions.
    sendCustomerConsentOtp: () => `customer/consent-otp`,
    verifyCustomerConsentOtp: () => `customer/consent-otp/verify`,
    getProgramDetails: (channelDefId: string) => `program/fetch?channelDefId=${channelDefId}`,
    customerOptOut: () => `customer/optout`,
    /**
     * The two coupon/deals keys the PARTNER conversation needs, moved here 2026-09-08 with
     * `packages/core/discounts`.
     *
     * `apiOfferRedeem` verifies an offer OTP on the offer-shaped channel; `unBlockDiscount`
     * releases a coupon the partner has blocked against the customer. Both are reached from
     * capability profiles now (`verifyChannel`, `requiresCouponUnblock`), so a partner on
     * either platform can need them.
     *
     * ⚠️ `customerCoupon` (`customer/offers`) deliberately STAYS web-only: its only caller is
     * `components/XenoModal`, a web POS surface, and it is not part of the discount flow.
     */
    apiOfferRedeem: () => `sale/customer/offer/redeem`,
    unBlockDiscount: () => `sale/customer/offer/unblock`,
  },
  TABLEVIEW: {
    getAllTablesData: () => "sale/resource/fetchAll",
    getResourceGroup: () => "resourcegroup/branch/fetchAll",
    getResource: () => "resource/branch/fetchAll",
    getPrinters: () => "printer/fetch",
    getTableOrders: (tableID: string) => `sale/resource/${tableID}`,
    tranferKOT: () => "sale/ticket/transfer",
    tranferItem: () => "sale/items/transfer",
    transferTable: () => "sale/transfer",
    cancelKOT: (id: string, ticket: string) => `sale/${id}/ticket/${ticket}/cancel`,
    cancelOrder: (id: string) => `sale/${id}/cancel`,
    splitOrders: () => "sale/split",
    closeOrder: () => "sale/close",
    sendBillOnEmail: (id: string, email: string) => `sale/${id}/email/${email}`,
    sendBillOnSMS: (id: string) => `sale/${id}/sms`,
    sendBillOnWhatsapp: (id: string) => `sale/${id}/wa`,
    print: () => "sale/print",
    printClosed: (id: string) => `sale/${id}/print`,
    users: () => "preferences/users",
    printKOTURL: (id: string) => `sale/${id}/ticket/printstatus`,
    getReservationById: (id: string) => `reservation/${id}`,
  },
  MYORDER: {
    getMyOrders: () => "sale/filter",
    // Was `() => "/open/view"` — a DEAD entry (zero callers, repo-wide) that was also wrong:
    // it returned a fragment, while both `saga/myOrder` variants hardcoded the real path
    // `sale/${saleId}/open/view` inline. Same duplicate-as-a-literal shape as
    // `updateOrderAction`, found by `scripts/endpoint-literals.mjs`. Corrected to the URL both
    // platforms actually request, and both sagas now call it. Changing the signature is safe
    // precisely because nothing referenced the old one.
    getOrderData: (saleId: string) => `sale/${saleId}/open/view`,
    updateSoldOutItem: () => "sale/item/outofstock",
    // the two fulfillment URLs v1's workers hardcoded in both variants (plan §10.6.2 row 6);
    // v2's sagas take them from here, v1's keep their literals until those workers retire
    updateFulfillment: (saleId: string) => `sale/${saleId}/fulfillment/update`,
    updateFulfillmentByBill: () => "sale/fulfillment/update",
    getCustomerDetails: () => "aggregator/action",
    voidSale: (id: string) => `sale/${id}/void`,
    unvoidSale: (id: string) => `sale/${id}/unvoid`,
    uncancelSale: (id: string) => `sale/${id}/uncancel`,
    swiggyCallApi: (id: string) => `sale/${id}/external/contact`,
    feedbackApi: () => 'feedback/campaign/sale/channel/link',
    approvalStatus: () => 'sale/approval/status',
    assignRiderApi: (saleId: string) => `sale/${saleId}/delivery/assign`,
    checkDeliveryPartner: (saledId: string) => `sale/delivery/orders/assigned?saleId=${saledId}`,
    cancelRiderApi: (saleId: string) => `sale/${saleId}/deliverytask/cancel`,
    // ── Adopted from web ──────────────────────────────────────────────────────────────────
    // Required by the nine myOrder saga workers that moved to `saga/myOrder/shared.ts`. Plain
    // URL builders with no platform difference possible, so they belong here rather than in a
    // variant.
    //
    // `updateOrderAction` is here for a different reason, and it is worth stating: it was NOT
    // web-only. `saga/myOrder/index.native.ts` was calling the same URL as a HARDCODED STRING,
    // so the seed rule ("only what shared code calls so far") mis-fired on a false premise —
    // absent from the native endpoint map is not the same as unused on native. Two definitions
    // of one URL is exactly the drift this package exists to end.
    //
    // `createTicket` moved here from `index.desktop.ts` on 2026-09-15 with `handleCreateKOT`
    // (helpers/saleHelpers) — the moment the old comment said tsc would force.
    createTicket: (id: string) => `sale/${id}/ticket/create`,
    updateOrderAction: () => "sale/nextAction/update",
    saleReturnApi: () => `sale/return/calculate`,
    generateAggregatorBillApi: (saleId: string) => `sale/${saleId}/aggregator-bill/generate`,
    checkChatSupportAccess: (outletId: string) => `aggregator/outlet/${outletId}/config`,
    getChatSupportUrl: () => `aggregator/outlet/support/chat`,
  },
  ITEMSTATUS: {
    getAllSoldOutItems: () => "item/outofstock/outOfStockItems",
    prepareItems: () => "item/outofstock/prepare",
    refreshItems: () => "item/outofstock/refresh",
  },
  PRINTER: {
    fetchAllPrinter: () => `../printer/fetch`,
    savePrinter: () => `../printer/save`,
    deletePrinter: (id: string) => `../printer/delete/${id}`,
    /**
     * Unprinted sale TICKETS and/or ITEM LABELS, selected by the query string
     * (`getTicket=1`, `getItemLabel=1`), returning `{ ticketSales, itemLabelSales }`.
     *
     * Moved here from `index.desktop.ts` 2026-09-12, when the other platform's poller gained its
     * label half and switched to this URL.
     *
     * ⚠️ It REPLACED a second key, `fetchUnprintedTickets: "sale/ticket/unprinted"`, which
     * returned a bare array of ticket sales and had no way to ask for labels. That key was
     * deleted in the same change because nothing referenced it any more — the offline path does
     * not use it either (it reads local storage through `helper/OfflineHelper`). If a ticket-only
     * fetch is ever wanted again, it is `getTicket=1` on this endpoint, not a second URL.
     */
    fetchUnprinted: (queryString = "") => `/sale/unprinted?${queryString}`,
    getLabelPrintStatusApi: (mid: string) => `sale/${mid}/itemlabel/print/status`,
  },
  OUTLETSTATUS: {
    getAllOutlets: () => "aggregator/configuration/filter",
    fetchActivities: () => "log/activity/filter",
    toggleOutletStatus: () => "aggregator/action",
    fetchOutletStatus: () => "aggregator/action",
    fetchActivityError: (key: string) => `log/activity/${key}/response`,
    bulkOutletToggle: () => "aggregator/bulk/action",
    getOutletDeliveryStatus: () => "aggregator/action",
    updateOutletDeliveryStatus: () => "aggregator/action",
    fetchHelpLink: () => "aggregator/action",
    getBranchData: (branchId: string) => `branch/${branchId}`,
  },
  SUPPORT: {
    postQuery: () => "support/tickets/create",
    getQuery: () => "support/tickets",
    getQueryByZohoId: (zohoTicketId: string) => `support/tickets/${zohoTicketId}`,
    getQueryLimit: () => "support/limit",
    getZohoUser: () => "support/zoho/contact",
  },
  SALESUMMARY: {
    getsaleSummary: () => `reports/sales/summary/print`,
  },
  INTEGRATEDPAYMENT: {
    getPaytmEdc: (pid: string) => `payment/provider/${pid}/configuration`,
  },
  CASH_REGISTER: {
    openCashRegister: "settlement/current/open",
    closeCashRegister: "settlement/current/close",
    getCashSettlementDetails: "settlement/current",
  },
  SALESYNC: {
    startSync: () => `offline/sync/start`,
    syncProcess: () => `offline/sale/process`,
    stopSync: () => `offline/sync/end`,
    fetchSale: (key: string) => `sale/${key}/open/view`,
  },
  NOTIFICATIONS: {
    getAllOtherNotifications: () => "notification/message/filter",
    markAsReadNotification: () => "notication/message/status/update",
    markAllAsRead: () => "notification/all/read",
    viewOrderBill: (messageId: string) => `sale/${messageId}/open/view`,
    acceptApproveOrder: (messageId: string) => `notification/message/${messageId}/approve`,
    rejectApproveOrder: (messageId: string) => `notification/message/${messageId}/reject`,
    viewPurchaseOrder: (messageId: string) => `inventory/activity/${messageId}/view`,
    viewFeedback: (messageId: string) => `feedback/response/${messageId}`,
    getAllNewOnlineOrders: () => "sale/notification/filter",
  },
  RECOMMENDER: {
    getRecommenderConfig: () => `recommendation/items/configuration`,
    getRecommendations: () => `recommendation/items/fetch`,
    /**
     * V2. SHARED as of the Suggestive Selling convergence — both platforms run the same flow and
     * the same saga now. Unlike its two siblings this one is NOT served from the business API: the
     * request sets `recommendationEngine: true`, which makes `utils/apiService`'s request
     * interceptor swap the base URL to the recommendation engine on both platforms.
     */
    getRecommendationsV2: () => `item/recommendations/cart/fetch/v2`,
  },
  CUSTOMER_DISPLAY: {
    getCustomerDisplayCampaign: (customerDisplayId: any) => `/customerdisplay/${customerDisplayId}/fetch`,
  },
  // DotPe's enquiry API (outside rista-be), read by `helpers/feedbackHelper`'s `captureInterest`. Moved here from
  // index.desktop.ts on 2026-10-27, when mobile began sending it (RESERVATIONS_V2_PLAN.md RV5d).
  DOTPE: {
    enquiry: `api/merchant/external/enquiry`,
  },
};
